// src/components/AttachmentsPanel.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import styles from '../Styles/Servicerequests.module.css';

const ALLOWED = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];
const MAX = 20 * 1024 * 1024; // 5MB

export default function AttachmentPanel({
  attachments = [],
  onUpload,
  onDelete,
  disabled = false,
  resetKey, // vaihtuu kun pyyntö vaihtuu -> reset input
}) {
  const dropRef = useRef(null);
  const fileRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [deletingIds, setDeletingIds] = useState(() => new Set());

  // resetoi paikallinen tila kun tiketti vaihtuu
  useEffect(() => {
    setUploading(false);
    setDeletingIds(new Set());
    if (fileRef.current) fileRef.current.value = '';
  }, [resetKey]);

  // drag&drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;

    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = (e) => {
      prevent(e);
      if (disabled || uploading) return;
      const files = Array.from(e.dataTransfer.files || []);
      handleFiles(files);
    };

    ['dragenter','dragover','dragleave','drop'].forEach(ev => el.addEventListener(ev, prevent));
    el.addEventListener('drop', onDrop);
    return () => {
      ['dragenter','dragover','dragleave','drop'].forEach(ev => el.removeEventListener(ev, prevent));
      el.removeEventListener('drop', onDrop);
    };
  }, [disabled, uploading, resetKey]);

  const handleFiles = useCallback(async (files) => {
    const valid = files.filter(f => (ALLOWED.includes(f.type) || (f.type === '' && f.name)) && f.size <= MAX);
    const rejected = files.filter(f => !valid.includes(f));
    if (rejected.length) {
      window.alert(
        'Jotkin tiedostot hylättiin.\n\n' +
        'Sallitut tiedostot: PDF, Word, Excel, kuvat\n' +
        'Maksimikoko: 20 Mt per tiedosto.'
      );
    }
    if (!valid.length) return;

    setUploading(true);
    try {
      await onUpload?.(valid);
    } finally {
      setUploading(false);
      // mahdollista valita sama tiedosto heti uudelleen
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [onUpload]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files).finally(() => {
      e.target.value = '';
    });
  };

  const handleDelete = useCallback(async (id) => {
    if (!id) return;
    setDeletingIds(prev => {
      const next = new Set(prev); next.add(id); return next;
    });
    try {
      await onDelete?.(id);
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev); next.delete(id); return next;
      });
    }
  }, [onDelete]);

  const isDeleting = (id) => deletingIds.has(id);

  return (
    <div className={styles.formRow}>
      {/* Dropzone – ei onClickiä kontissa */}
      <div
        className={styles.dropzoneArea}
        ref={dropRef}
        role="button"
        tabIndex={0}
        aria-disabled={disabled || uploading}
        style={(disabled || uploading) ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
      >
        <span>Pudota liitteet tähän</span>
        <span className={styles.dropOr}> TAI </span>

        {/* Yksin vastuussa tiedostonvalitsimen avaamisesta */}
        <label
          className={styles.browseBtn}
          onClick={(e) => e.stopPropagation()}
        >
          {uploading ? 'Ladataan…' : 'SELAA'}
          <input
            ref={fileRef}
            type="file"
            multiple
            onChange={onPick}
            disabled={disabled || uploading}
          />
        </label>
      </div>

      {/* Pieni statusrivi ruudunlukijoille ja visuaalinen vihje */}
      <div aria-live="polite" className={styles.mutedSmall}>
        {uploading ? 'Tiedostoja ladataan…' : ''}
      </div>

      {(attachments || []).length > 0 && (
        <div className={styles.docsList}>
          {attachments.map(att => (
            <div key={att.id} className={styles.docItem}>
              <a href={att.url} target="_blank" rel="noreferrer" className={styles.docLink}>{att.name}</a>
              <span className={styles.mutedSmall}>
                {typeof att.size === 'number' ? ` (${Math.ceil(att.size / 1024)} kt)` : ''}
              </span>
              <button
                className={styles.deleteBtn}
                onClick={() => handleDelete(att.id)}
                disabled={disabled || isDeleting(att.id)}
              >
                {isDeleting(att.id) ? 'Poistetaan…' : 'Poista'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
